import React, { useEffect, useState } from 'react';
import { Section, SectionProps } from '@jbhi-fi/components/src/Section/Section';
import { ContentBlock } from '@jbhi-fi/components/src/ContentBlock';
import { useHomepageProps } from '../../hooks/content/useHomepageProps';
import { RedirectWelcomeAlert } from '../../components/RedirectWelcomeAlert/RedirectWelcomeAlert';
import { HeroBlock, HeroBlockProps } from '@jbhi-fi/components/src/HeroBlock';
import { Disclaimer } from '@jbhi-fi/components/src/Disclaimer';
import { ContentTheme } from '@jbhi-fi/components/src/Contentful/transformers/ThemeTransformer';
import { HeadingBlock } from '@jbhi-fi/components/src/HeadingBlock/HeadingBlock';
import { HeadingDividerVariant } from '@jbhi-fi/components/src/HeadingDivider/HeadingDivider';
import { styles } from './Home.css';
import { ShowcaseCardProps } from '@jbhi-fi/components/src/ShowcaseCard/ShowcaseCard';
import { ListingBlock, ListingBlockProps } from '@jbhi-fi/components/src/ListingBlock';
import { ShowcaseCarousel } from '@jbhi-fi/components/src/ShowcaseCarousel/ShowcaseCarousel';
import CrazyFont from '@jbhi-fi/lanyard-ui/font/CrazyFont';

export type HomeProps = {
  heading: string;
  subText?: string;
  promoCardCarousel?: ListingBlockProps;
  showcaseCards?: Array<ShowcaseCardProps>;
  type?: 'Showcase' | 'Editorial';
  hero?: HeroBlockProps;
  sections: Array<SectionProps>;
  contentPalette?: ContentTheme['palette'];
};

export const Home = () => {
  const {
    data: { heading, subText, hero, sections, contentPalette, promoCardCarousel, showcaseCards, type } = {
      heading: '',
      subText: '',
      hero: {},
      sections: [],
      contentPalette: {} as ContentTheme['palette'],
    },
    isLoading,
    error,
  } = useHomepageProps() || {};
  const isShowcase = type === 'Showcase';
  const hasShowcaseCards = !!showcaseCards?.length;
  const [showRedirectedAlert, setShowRedirectedAlert] = useState<boolean>(false);

  useEffect(() => {
    const urlSearchParams = new URL(window.location.href).searchParams;
    if (urlSearchParams.has('redirected')) {
      setShowRedirectedAlert(true);
      urlSearchParams.delete('redirected');
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${urlSearchParams.toString() ? `?${urlSearchParams.toString()}` : ''}`
      );
    }
  }, []);

  return isLoading || error ? null : (
    <>
      <ContentBlock
        paddingTop={15}
        style={{ backgroundColor: contentPalette?.background }}
        paddingBottom={{ mobile: 4, tablet: 15 }}
        {...(isShowcase && { maxWidth: { xl: 'xlarge', xxxl: 'ultralarge' } })}
      >
        {heading ? (
          <HeadingBlock
            heading={heading}
            subText={subText}
            contentPalette={contentPalette}
            headingStyle="Centred"
            variant={HeadingDividerVariant.large}
            className={styles.heading}
            as="h1"
          />
        ) : null}
        {isShowcase && promoCardCarousel ? (
          <div className={hasShowcaseCards ? styles.showcaseContainer : styles.showcaseContainerFullWidth}>
            <div className={styles.showcaseListingWrapper}>
              <ListingBlock {...promoCardCarousel} />
            </div>

            {hasShowcaseCards ? (
              <>
                <CrazyFont as="p" variant="withShadowAndOutline" className={styles.showcaseCarouselHeading}>
                  Coming Soon!
                </CrazyFont>
                <div className={styles.showcaseCarouselWrapper}>
                  <ShowcaseCarousel showcaseCards={showcaseCards || []} />
                </div>
              </>
            ) : null}
          </div>
        ) : (
          <HeroBlock {...hero} />
        )}
      </ContentBlock>
      <ContentBlock
        paddingTop={0}
        paddingRight={0}
        paddingLeft={0}
        paddingBottom={{ mobile: 15, tablet: 20 }}
        maxWidth="full"
      >
        {sections?.map((section: SectionProps, i: number) => (
          <Section key={i} {...section} />
        ))}
        <ContentBlock
          paddingTop={15}
          paddingBottom={0}
          paddingX={{ xs: 4, md: 8 }}
          maxWidth={{ mobile: 'xlarge', xxxl: 'ultralarge' }}
        >
          <Disclaimer disclaimers={window.Jbhifi.disclaimers} />
        </ContentBlock>
      </ContentBlock>
      <RedirectWelcomeAlert
        heading={
          <>
            Exciting news... <em>STACK</em> has moved!
          </>
        }
        content="We’ve moved in with the JB fam to bring you an even bigger and better one-stop hub for the best in home entertainment, latest tech, exclusive competitions, guides, tips, and heaps more. Whether you’re a long time reader or first time caller, we think it’s worth a bookmark."
        isVisible={showRedirectedAlert}
        onClose={() => setShowRedirectedAlert(false)}
      />
    </>
  );
};
